// frontend/src/pages/StoreSettingsPage.jsx
import React, { useState, useEffect } from 'react';
import api from '../services/api'; // Use centralized API service

const StoreSettingsPage = () => {
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchDefaultStore = async () => {
      try {
        const res = await api.get('/api/stores');
        if (res.data.length === 0) {
          console.warn('No connected stores found for user.');
          setLoading(false);
          return;
        }
        const defaultStore = res.data[0];
        setSelectedStoreId(defaultStore.id);
      } catch (err) {
        console.error('Failed to fetch user store', err);
        setLoading(false);
      }
    };
    fetchDefaultStore();
  }, []);

  useEffect(() => {
    if (!selectedStoreId) return;
    const fetchSettings = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/api/settings/${selectedStoreId}`);
        setSettings(res.data);
      } catch (err) {
        console.error('Failed to load settings', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [selectedStoreId]);

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!selectedStoreId || !settings) return;
    setSaving(true);

    try {
      const payload = {
        ...settings,
        profitMargin: parseFloat(settings.profitMargin) || 1.0,
      };

      await api.post(`/api/settings/${selectedStoreId}`, payload);
      alert('Settings saved successfully!');
    } catch (err) {
      console.error('Failed to save settings', err.response?.data || err.message);
      alert('Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !settings) {
    return <div className="p-6">Loading settings...</div>;
  }

  if (!selectedStoreId) {
    return <div className="p-6">No store connected. Please connect a store first.</div>;
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h2 className="text-2xl font-semibold mb-4">Store One-Click Import Settings</h2>

      {!settings ? (
        <p>Loading settings...</p>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block font-medium text-gray-700">Profit Margin</label>
            <input
              type="number"
              step="5"
              min="0"
              max="300"
              value={settings.profitMargin ? (settings.profitMargin * 100).toFixed(0) : ''}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                handleChange('profitMargin', isNaN(val) ? null : val / 100);
              }}
              className="w-full border border-gray-300 rounded-md p-2"
            />
            <small className="text-gray-500">This defines your markup. 100% = 2× price.</small>
          </div>

          <div>
            <label className="block font-medium text-gray-700">Language</label>
            <select
              value={settings.language || 'en'}
              onChange={(e) => handleChange('language', e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2"
            >
              <option value="en">English</option>
              <option value="fr">French</option>
              <option value="es">Spanish</option>
              <option value="de">German</option>
              <option value="zh">Chinese</option>
            </select>
          </div>

          <div className="space-y-4 mt-6">
            <h3 className="text-lg font-medium text-gray-900">Image Processing</h3>

            <div className="space-y-3 pl-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="enrichWithGemini"
                  checked={settings.enrichWithGemini || false}
                  onChange={(e) => handleChange('enrichWithGemini', e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="enrichWithGemini" className="ml-2 block text-sm text-gray-700">
                  Enable text extraction with Gemini
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="generateAIImages"
                  checked={settings.generateAIImages || false}
                  onChange={(e) => handleChange('generateAIImages', e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="generateAIImages" className="ml-2 block text-sm text-gray-700">
                  Enable AI Image Enhancement
                  <p className="text-xs text-gray-500 mt-1">
                    Uses AI to enhance product images by improving quality and removing backgrounds
                  </p>
                </label>
              </div>
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="mt-4 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      )}
    </div>
  );
};

export default StoreSettingsPage;
